import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { MaterialReactTable } from "material-react-table";

const getColumnId = (column, index) => column?.name || column?.label || `column_${index}`;

const LegacyMuiDataTable = ({ columns = [], data = [], options = {}, title }) => {
  const tableProps = typeof options.setTableProps === "function" ? options.setTableProps() : {};

  const mappedColumns = useMemo(() => {
    return columns.map((column, index) => {
      const columnOptions = column?.options || {};
      const columnId = getColumnId(column, index);
      const accessorKey = column?.name || null;

      const cellRenderer = columnOptions.customBodyRenderLite
        ? ({ row }) => columnOptions.customBodyRenderLite(row.index)
        : columnOptions.customBodyRender
          ? ({ row, cell }) =>
              columnOptions.customBodyRender(cell.getValue(), row.index, row.original)
          : undefined;

      const columnDef = {
        id: accessorKey ? undefined : columnId,
        accessorKey: accessorKey || undefined,
        accessorFn: accessorKey ? undefined : () => null,
        header: column?.label ?? accessorKey ?? columnId,
        enableColumnFilter: columnOptions.filter !== false,
        enableSorting: columnOptions.sort !== false,
        enableHiding: columnOptions.viewColumns !== false,
        Cell: cellRenderer,
        muiTableHeadCellProps:
          typeof columnOptions.setCellHeaderProps === "function"
            ? columnOptions.setCellHeaderProps()
            : undefined,
        muiTableBodyCellProps:
          typeof columnOptions.setCellProps === "function"
            ? columnOptions.setCellProps()
            : undefined,
      };

      if (columnOptions.filterList?.length) {
        columnDef.filterFn = "equals";
      }

      return columnDef;
    });
  }, [columns]);

  const initialState = useMemo(() => {
    const columnVisibility = {};
    const columnFilters = [];

    columns.forEach((column, index) => {
      const columnOptions = column?.options || {};
      const columnId = column?.name || getColumnId(column, index);

      if (columnOptions.display === false) {
        columnVisibility[columnId] = false;
      }

      if (columnOptions.filterList?.length && column?.name) {
        columnFilters.push({ id: column.name, value: columnOptions.filterList[0] });
      }
    });

    return {
      columnVisibility,
      columnFilters,
    };
  }, [columns]);

  const noMatchLabel = options?.textLabels?.body?.noMatch;

  return (
    <MaterialReactTable
      columns={mappedColumns}
      data={data}
      enableRowSelection={options.selectableRows !== "none"}
      enableStickyHeader={Boolean(options.fixedHeader)}
      enableHiding={options.viewColumns !== false}
      initialState={initialState}
      muiTableProps={tableProps}
      muiTableContainerProps={
        options.tableBodyHeight ? { sx: { maxHeight: options.tableBodyHeight } } : undefined
      }
      muiTableBodyRowProps={
        typeof options.setRowProps === "function"
          ? ({ row }) => options.setRowProps(row.original, row.index, row.index) || {}
          : undefined
      }
      renderEmptyRowsFallback={
        noMatchLabel ? () => <Box sx={{ p: 2 }}>{noMatchLabel}</Box> : undefined
      }
      renderTopToolbarCustomActions={
        title || options.customToolbar
          ? () => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {title ? <Typography variant="h6">{title}</Typography> : null}
                {typeof options.customToolbar === "function" ? options.customToolbar() : null}
              </Box>
            )
          : undefined
      }
    />
  );
};

export default LegacyMuiDataTable;
